import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Form, Input, InputNumber, Button, Select, Divider, notification } from 'antd';
import classnames from 'classnames';
import Card from '../../common/components/Card';
import { gql } from 'graphql-tag';
import { useQuery, useMutation } from 'react-apollo';
import { UPDATE_USER } from '../mutations';

type UserDetailsFormProps = {
  userId: string;
};

const schema = z.object({
  firstName: z.string().min(1, 'First name is required').trim(),
  lastName: z.string().min(1, 'Last name is required').trim(),
  title: z.string().trim().nullish(),
  department: z.string().trim().nullish(),
  payrollId: z.string().trim().nullish(),
  role: z.string().nullish(),
  employeeType: z.string().nullish(),
  quickbooksEmployeeName: z.string().trim().nullish(),
  quickbooksPayrollItem: z.string().trim().nullish(),
  supervisorId: z.string().trim().nullish(),
  defaultRate: z.number().nullish(),
});

const GET_USER_DETAILS = gql`
  query GetUserDetails($userId: String!) {
    admin {
      user(userId: $userId) {
        _id
        firstName
        lastName
        payrollId
        title
        department
        role
        employeeType
        supervisorId
        defaultRate
        quickbooks {
          employeeName
          payrollItem
        }
      }
    }
  }
`;

const GET_TAXONOMY_ROLES = gql`
  query GetTaxonomyRoles {
    taxonomy(name: "Roles") {
      terms {
        name
      }
    }
  }
`;

const GET_ALL_USERS = gql`
  query GetAllUsers {
    users {
      _id
      firstName
      lastName
    }
  }
`;

const UserDetailsForm = ({ userId }: UserDetailsFormProps) => {
  const { data: userDetailsData, loading: userLoading, refetch } = useQuery(GET_USER_DETAILS, {
    variables: { userId },
    fetchPolicy: 'cache-and-network',
  });

  const { data: taxonomyData } = useQuery(GET_TAXONOMY_ROLES, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: allUsersData, loading: fetchingUsers } = useQuery(GET_ALL_USERS);

  const [updateUser, { loading: updateLoading }] = useMutation(UPDATE_USER, {
    onCompleted: () => {
      notification.success({
        message: 'User updated successfully',
      });
      refetch();
    },
    onError: (error) => {
      notification.error({
        message: 'Failed to update user',
        description: error.message,
      });
    },
  });

  const user = userDetailsData?.admin?.user;

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      payrollId: '',
      title: '',
      department: '',
      role: '',
      employeeType: '',
      quickbooksPayrollItem: '',
      quickbooksEmployeeName: '',
      supervisorId: '',
      defaultRate: null,
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        title: user.title || '',
        department: user.department || '',
        payrollId: user.payrollId || '',
        role: user.role || '',
        employeeType: user.employeeType || '',
        quickbooksPayrollItem: user.quickbooks?.payrollItem || '',
        quickbooksEmployeeName: user.quickbooks?.employeeName || '',
        supervisorId: user.supervisorId || '',
        defaultRate: user.defaultRate || null,
      });
    }
  }, [reset, user]);

  const onSubmit = (data: any) => {
    updateUser({
      variables: {
        userId,
        firstName: data.firstName,
        lastName: data.lastName,
        title: data.title,
        department: data.department,
        role: data.role,
        payrollId: data.payrollId,
        employeeType: data.employeeType,
        quickbooksEmployeeName: data.quickbooksEmployeeName,
        quickbooksPayrollItem: data.quickbooksPayrollItem,
        supervisorId: data.supervisorId,
        defaultRate: data.defaultRate,
      },
    });
  };

  return (
    // @ts-ignore
    <Card title="Edit Information" floating border padded>
      <form
        className={classnames('flex flex-wrap transition-opacity', {
          'opacity-50': userLoading || updateLoading,
        })}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Form.Item
          className="w-full md:w-1/2 p-2"
          label="First Name"
          hasFeedback
          validateStatus={errors.firstName?.message && 'error'}
          help={errors.firstName?.message}
        >
          <Controller
            name="firstName"
            control={control}
            // @ts-ignore
            render={({ field }) => <Input {...field} />}
          />
        </Form.Item>
        <Form.Item
          className="w-full md:w-1/2 p-2"
          label="Last Name"
          hasFeedback
          validateStatus={errors.lastName?.message && 'error'}
          help={errors.lastName?.message}
        >
          <Controller
            name="lastName"
            control={control}
            // @ts-ignore
            render={({ field }) => <Input {...field} />}
          />
        </Form.Item>
        <Form.Item
          className="w-full md:w-1/2 p-2"
          label="Title"
          hasFeedback
          validateStatus={errors.title?.message && 'error'}
          help={errors.title?.message}
        >
          <Controller
            name="title"
            control={control}
            // @ts-ignore
            render={({ field }) => <Input {...field} />}
          />
        </Form.Item>
        <Form.Item
          className="w-full md:w-1/2 p-2"
          label="Department"
          hasFeedback
          validateStatus={errors.department?.message && 'error'}
          help={errors.department?.message}
        >
          <Controller
            name="department"
            control={control}
            // @ts-ignore
            render={({ field }) => <Input {...field} />}
          />
        </Form.Item>
        <Form.Item
          className="w-full md:w-1/2 p-2"
          label="Payroll ID"
          hasFeedback
          validateStatus={errors.payrollId?.message && 'error'}
          help={errors.payrollId?.message}
        >
          <Controller
            name="payrollId"
            control={control}
            // @ts-ignore
            render={({ field }) => <Input {...field} />}
          />
        </Form.Item>
        <Form.Item
          className="w-full md:w-1/2 p-2"
          label="Employee Type"
          hasFeedback
          validateStatus={errors.employeeType?.message && 'error'}
          help={errors.employeeType?.message}
        >
          <Controller
            name="employeeType"
            control={control}
            // @ts-ignore
            render={({ field }) => (
              // @ts-ignore
              <Select showSearch placeholder="Select an employee type" {...field}>
                {/* @ts-ignore */}
                <Select.Option value="W-2">W-2</Select.Option>
                {/* @ts-ignore */}
                <Select.Option value="C2C">C2C</Select.Option>
              </Select>
            )}
          />
        </Form.Item>
        {!fetchingUsers && (
          <Form.Item
            className="w-full md:w-1/2 p-2"
            label="Supervisor"
            validateStatus={errors.supervisorId?.message && 'error'}
            help={errors.supervisorId?.message}
          >
            <Controller
              name="supervisorId"
              control={control}
              // @ts-ignore
              render={({ field }) => {
                return (
                  // @ts-ignore
                  <Select
                    showSearch
                    placeholder="Select a supervisor"
                    {...field}
                    filterOption={(input: string, option: any) => {
                      const searchText = input.toLowerCase();
                      const fullName = String(option?.props?.children || '').toLowerCase();
                      return fullName.includes(searchText);
                    }}
                  >
                    {allUsersData?.users?.map((user: any) => (
                      // @ts-ignore
                      <Select.Option
                        key={user._id}
                        value={user._id}
                      >
                        {`${user.firstName} ${user.lastName}`}
                      </Select.Option>
                    ))}
                  </Select>
                );
              }}
            />
          </Form.Item>
        )}
        <Form.Item
          className="w-full md:w-1/2 p-2"
          label="Role"
          validateStatus={errors.role?.message && 'error'}
          help={errors.role?.message}
        >
          <Controller
            name="role"
            control={control}
            // @ts-ignore
            render={({ field }) => (
              // @ts-ignore
              <Select showSearch placeholder="Select a role" {...field}>
                {taxonomyData?.taxonomy?.terms?.map((role: any) => (
                  // @ts-ignore
                  <Select.Option key={role.name} value={role.name}>
                    {role.name}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
        </Form.Item>
        <Form.Item
          className="w-full md:w-1/2 p-2"
          label="Default Rate"
          hasFeedback
          validateStatus={errors.defaultRate?.message && 'error'}
          help={errors.defaultRate?.message}
        >
          <Controller
            name="defaultRate"
            control={control}
            // @ts-ignore
            render={({ field }) => <InputNumber {...field} />}
          />
        </Form.Item>

        <Divider>Quickbooks</Divider>
        <Form.Item
          className="w-full md:w-1/2 p-2"
          label="Employee Name"
          hasFeedback
          validateStatus={errors.quickbooksEmployeeName?.message && 'error'}
          help={errors.quickbooksEmployeeName?.message}
        >
          <Controller
            name="quickbooksEmployeeName"
            control={control}
            // @ts-ignore
            render={({ field }) => <Input {...field} />}
          />
        </Form.Item>
        <Form.Item
          className="w-full md:w-1/2 p-2"
          label="Payroll Item"
          validateStatus={errors.quickbooksPayrollItem?.message && 'error'}
          help={errors.quickbooksPayrollItem?.message}
        >
          <Controller
            name="quickbooksPayrollItem"
            control={control}
            // @ts-ignore
            render={({ field }) => (
              // @ts-ignore
              <Select showSearch placeholder="Select payroll item" {...field}>
                {/* @ts-ignore */}
                <Select.Option value="Salary">Salary</Select.Option>
                {/* @ts-ignore */}
                <Select.Option value="Hourly">Hourly</Select.Option>
              </Select>
            )}
          />
        </Form.Item>
        <div className="w-full flex">
          <Form.Item className=" p-2">
            <Button
              type="primary"
              disabled={!isDirty}
              loading={userLoading || updateLoading}
              htmlType="submit"
            >
              Update
            </Button>
          </Form.Item>
          <Form.Item className=" p-2">
            <Button
              disabled={!isDirty}
              onClick={() => {
                reset();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </div>
      </form>
    </Card>
  );
};

export default UserDetailsForm;